import { useState, useEffect, useRef } from "react";
import { ChevronDown, Eye, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function RolePreviewSwitcher() {
  const { me, persona, setPersona } = useAuth();
  const [open, setOpen] = useState(false);
  const [personas, setPersonas] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/preview/personas").then((r) => setPersonas(r.data.personas)).catch(() => {});
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const currentId = persona || "super_admin";
  const currentLabel = personas.find((p) => p.id === currentId)?.label || "Super Admin";

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="role-preview-trigger"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg border border-ink/20 bg-cream text-ink text-[14px] font-semibold min-h-[44px] hover:bg-ink/5"
      >
        <Eye className="w-4 h-4 text-maroon shrink-0" />
        <span className="hidden sm:inline">Viewing as:</span>
        <span className="hidden sm:inline truncate max-w-[160px]">{currentLabel}</span>
        <ChevronDown className="w-4 h-4 opacity-60 shrink-0" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-60 bg-cream border border-ink/20 rounded-xl shadow-xl z-50 p-1.5" data-testid="role-preview-menu">
          <div className="px-3 py-2 text-[11px] uppercase tracking-widest text-ink/50 font-bold">Preview a role</div>
          {personas.map((p) => (
            <button
              key={p.id}
              data-testid={`preview-persona-${p.id}`}
              onClick={() => { setPersona(p.id === "super_admin" ? null : p.id); setOpen(false); }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left text-[15px] min-h-[44px] transition-colors ${currentId === p.id ? "bg-maroon text-cream font-semibold" : "text-ink hover:bg-ink/10"}`}
            >
              <span>{p.label}</span>
              {currentId === p.id && <Check className="w-4 h-4 text-cream shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function PreviewBanner() {
  const { me, persona, setPersona } = useAuth();
  if (!me?.user?.is_super_admin || !persona || persona === "super_admin") return null;
  return (
    <div className="bg-maroon text-cream px-5 py-2.5 flex items-center gap-3 flex-wrap text-[15px]" data-testid="preview-banner">
      <Eye className="w-4 h-4" />
      <span>You are previewing Rosemoor as a <strong>{me.role.name}</strong> would see it.</span>
      <button
        data-testid="exit-preview-button"
        onClick={() => setPersona(null)}
        className="ml-auto underline font-semibold hover:text-gold"
      >
        Exit preview
      </button>
    </div>
  );
}
